import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { API_ENDPOINTS } from '@caro/shared/constants';
import type { AutoProvisionResult } from '@caro/shared/types';
import { useAuth, authStore, onDeviceAuthLost } from './useAuth';
import { KeepAliveBridge } from '../native/KeepAliveBridge';
import { logEvent } from '../utils/debugLog';
import { BOOT_RETRY, createRetryScheduler } from '../utils/retryBackoff';

type RetryScheduler = ReturnType<typeof createRetryScheduler>;

/**
 * Zero-touch device auto-provisioning (R1).
 *
 * Spec: specs/20260602-resident-device-autoprovision/plan.md §5 R1
 *
 * Device identity keys on the Hexnode UDID. It is resolved from three sources,
 * in order:
 *   1. the managed-launch URL param (`?hexnodeUDID=<udid>`) — the web/kiosk path;
 *   2. the persisted `sessionStorage` value (survives SPA navigation / refresh);
 *   3. native Managed App Configuration (`RestrictionsManager`, read via
 *      `KeepAliveBridge.getDeviceIds().managedUdid`) — the APK FLEET path, where
 *      Hexnode pushes `hexnodeUdid = %udid%` per device so there is no per-device
 *      build and no URL param. No-ops on web / when the MDM hasn't pushed it.
 * On cold start when no DeviceToken exists, a URL-supplied UDID is persisted to
 * `sessionStorage` and stripped from the URL via `history.replaceState` —
 * mirroring the public-alarm-token pattern in repo-root CLAUDE.md. It POSTs
 * `{ hexnodeUDID }` to `AUTH_AUTO_PROVISION`; on success it stores the minted
 * device JWT in the same auth store the qrActivate / AdminSetupPage flow uses
 * and routes to `/`.
 *
 * Fail-closed (no UDID present, or 404 / 409 / timeout) NEVER throws — it routes
 * to `/admin-setup` so the operator can complete the PIN fallback. AdminSetupPage
 * is unchanged.
 *
 * NOTE: this hook no longer depends on `NativeBridge.getDeviceInfo()` /
 * `@capacitor/device`; the UDID arrives via the managed-launch URL.
 */

export const HEXNODE_UDID_STORAGE_KEY = 'caro.hexnodeUDID';

export interface AutoProvisionDeps {
  /** True when a DeviceToken / access token is already stored. */
  hasToken: () => boolean;
  /** Raw URL query string, e.g. `window.location.search`. */
  getUrlSearch: () => string;
  /** Read the persisted UDID (sessionStorage). */
  getStored: (key: string) => string | null;
  /** Persist the UDID (sessionStorage). */
  setStored: (key: string, value: string) => void;
  /** Strip the `hexnodeUDID` param from the URL (history.replaceState). */
  stripUrl: () => void;
  /**
   * Read the Hexnode UDID delivered via native Managed App Configuration
   * (RestrictionsManager). Optional: absent in tests / on web. Resolves null
   * when not native or when the MDM has not pushed `hexnodeUdid`.
   */
  getManagedUdid?: () => Promise<string | null>;
  /** Optional diagnostic sink (in-app DebugOverlay). No-op in tests / when absent. */
  log?: (tag: string, data?: unknown) => void;
  /** POST helper (api.post). */
  post: <T>(url: string, body: unknown) => Promise<T>;
  /** Persist the minted device token (same store as qrActivate / admin flow). */
  storeToken: (token: string) => void;
  /**
   * Persist the user profile from the provision response. REQUIRED for the app
   * to become authenticated: the shared auth store only sets `isAuthenticated`
   * true via `setUser` (not `setAccessToken`), and a populated user also
   * suppresses the User-JWT `/me` hydration that a DeviceToken can't satisfy.
   */
  storeUser?: (user: unknown) => void;
  /** Router navigate. */
  navigate: (path: string) => void;
}

export interface AutoProvisionOutcome {
  provisioned: boolean;
  skipped?: boolean;
  /**
   * True when the failure was the SERVER's or the NETWORK's (timeout, 5xx,
   * 429), i.e. the same request may succeed later. False for a verdict the
   * server actually gave (400/404/409: not our device, ambiguous) and for
   * "no UDID" — retrying those changes nothing.
   */
  retryable?: boolean;
}

export interface AutoProvisionRunOptions {
  /** 0 on the first try; retries pass 1, 2, … so they do not re-navigate. */
  attempt?: number;
}

type AutoProvisionEnvelope = { success?: boolean; data?: AutoProvisionResult } & Partial<AutoProvisionResult>;

/** Server/network failures are retryable; explicit verdicts are not. */
export function isRetryableProvisionError(e: unknown): boolean {
  const status = (e as { response?: { status?: number } })?.response?.status;
  if (status === undefined || status === null) return true; // timeout / network / CORS
  return status >= 500 || status === 429 || status === 408;
}

/**
 * Pure, dependency-injected core of the auto-provision flow. Exported so it can
 * be unit-tested without mounting React.
 */
export async function runAutoProvision(
  deps: AutoProvisionDeps,
  options: AutoProvisionRunOptions = {},
): Promise<AutoProvisionOutcome> {
  const attempt = options.attempt ?? 0;
  // Whether the device is ALREADY authenticated. When true we run in "refresh"
  // mode: re-fetch the provisioning profile to pick up server-side changes
  // (UI language, building, extension) WITHOUT navigating, and — critically —
  // WITHOUT logging the device out if the refresh fails. When false this is a
  // cold start: success → '/', any failure → '/admin-setup' (manual PIN).
  const hadToken = deps.hasToken();
  let udidSource: 'url' | 'session' | 'managed' | 'none' = 'none';

  // 1. Read the UDID from the URL first; persist + strip when present.
  let hexnodeUDID: string | null = null;
  try {
    const params = new URLSearchParams(deps.getUrlSearch() || '');
    const fromUrl = params.get('hexnodeUDID');
    if (fromUrl && fromUrl.trim()) {
      hexnodeUDID = fromUrl.trim();
      udidSource = 'url';
      deps.setStored(HEXNODE_UDID_STORAGE_KEY, hexnodeUDID);
      deps.stripUrl();
    }
  } catch {
    // URL parsing failed — fall through to the persisted value.
  }

  // 2. Fall back to the persisted UDID (survives SPA navigation / refresh).
  if (!hexnodeUDID) {
    try {
      const cached = deps.getStored(HEXNODE_UDID_STORAGE_KEY);
      if (cached && cached.trim()) {
        hexnodeUDID = cached.trim();
        udidSource = 'session';
      }
    } catch {
      // storage unavailable — treat as no UDID.
    }
  }

  // 2b. FLEET path: read the Hexnode UDID delivered via native Managed App
  // Configuration. This is how the APK fleet provisions hands-free — Hexnode
  // pushes `hexnodeUdid = %udid%` per device; no URL param, no per-device build.
  // No-ops on web and when the MDM hasn't pushed the value (resolves empty).
  if (!hexnodeUDID && deps.getManagedUdid) {
    try {
      const managed = await deps.getManagedUdid();
      if (managed && managed.trim()) {
        hexnodeUDID = managed.trim();
        udidSource = 'managed';
        deps.setStored(HEXNODE_UDID_STORAGE_KEY, hexnodeUDID);
      }
    } catch {
      // native bridge unavailable — treat as no UDID.
    }
  }

  deps.log?.('provision.resolve', { source: udidSource, udid: hexnodeUDID, hadToken });

  // 3. No UDID anywhere.
  if (!hexnodeUDID) {
    deps.log?.('provision.noUdid', { hadToken, action: hadToken ? 'keep-session' : 'admin-setup' });
    // Cold start with no UDID → manual PIN flow. Already-authed (no UDID to
    // refresh with, e.g. an email/password session) → leave the session alone.
    if (!hadToken) deps.navigate('/admin-setup');
    return { provisioned: false };
  }

  try {
    deps.log?.('provision.request', { endpoint: API_ENDPOINTS.AUTH_AUTO_PROVISION, udid: hexnodeUDID });
    const res = await deps.post<AutoProvisionEnvelope>(API_ENDPOINTS.AUTH_AUTO_PROVISION, {
      hexnodeUDID,
    });

    const result = (res?.data || res) as (AutoProvisionResult & { user?: unknown }) | undefined;
    const token = result?.deviceToken;

    if (!token) {
      deps.log?.('provision.noToken', { hadToken, action: hadToken ? 'keep-session' : 'admin-setup' });
      if (!hadToken) deps.navigate('/admin-setup');
      return { provisioned: false };
    }
    deps.log?.('provision.success', {
      reauth: hadToken,
      extension: (result as { extension?: unknown })?.extension,
      person: (result as { person?: { id?: unknown } })?.person?.id,
    });

    // Store the (possibly refreshed) token + profile. setUser flips
    // `isAuthenticated` true and refreshes language/building/etc.
    deps.storeToken(token);
    if (result?.user && deps.storeUser) {
      deps.storeUser(result.user);
    }
    // Only navigate on a cold start; a refresh must not yank the user around.
    if (!hadToken) deps.navigate('/');
    return { provisioned: true };
  } catch (e) {
    // Surface the server's failure detail (status + reason) so the DebugOverlay
    // shows WHY provisioning failed (e.g. 404 no_match = UDID not in the DB).
    const err = e as { message?: string; response?: { status?: number; data?: { reason?: string; message?: string } } };
    const retryable = !hadToken && isRetryableProvisionError(e);
    deps.log?.('provision.error', {
      status: err?.response?.status,
      reason: err?.response?.data?.reason,
      message: err?.response?.data?.message || err?.message,
      hadToken,
      attempt,
      retryable,
      action: hadToken ? 'keep-session' : 'admin-setup',
    });
    // Cold start failure → manual PIN fallback, shown ONCE (a retry that fails
    // again must not keep re-navigating under an operator typing a PIN).
    // Refresh failure (we already had a valid session) → keep the existing
    // session; do NOT log out / redirect.
    if (!hadToken && attempt === 0) deps.navigate('/admin-setup');
    return { provisioned: false, skipped: hadToken, retryable };
  }
}

/**
 * Cold-start auto-provision WITH retry. Runs `runAutoProvision` and, when the
 * failure was the server's or the network's, tries again with backoff
 * (5 s → 60 s, indefinitely) until it succeeds, a token appears by another
 * route (operator PIN on /admin-setup), or `stop()` is called.
 *
 * ── WAAROM ─────────────────────────────────────────────────────────────────
 *
 * Eén poging bij het opstarten was precies het gat waar de vloot doorviel op
 * 2026-09-23: tablets herstart terwijl de DB nog in herstel zat kregen een
 * 500/timeout, gingen naar /admin-setup, en bleven daar staan tot iemand ze
 * nogmaals fysiek herstartte. Een verdict van de server (404/409) blijft
 * definitief — daar helpt herhalen niets en dat is de PIN-flow.
 *
 * Returns a `stop()` for tests / teardown. The React hook deliberately never
 * calls it on unmount: the module-level guard already makes this once-per-page
 * -load, and a remount must not kill a retry in flight.
 */
export function startAutoProvision(
  deps: AutoProvisionDeps,
  scheduler: RetryScheduler = createRetryScheduler(BOOT_RETRY),
): { stop: () => void; done: Promise<AutoProvisionOutcome> } {
  let stopped = false;
  let resolveDone: (o: AutoProvisionOutcome) => void = () => {};
  const done = new Promise<AutoProvisionOutcome>((r) => { resolveDone = r; });

  const run = async (attempt: number): Promise<void> => {
    const outcome = await runAutoProvision(deps, { attempt });
    if (stopped) return;
    if (outcome.provisioned || !outcome.retryable || deps.hasToken()) {
      resolveDone(outcome);
      return;
    }
    const delayMs = scheduler.schedule(() => { void run(attempt + 1); });
    deps.log?.('provision.retry', { nextAttempt: attempt + 1, delayMs });
  };

  void run(0);
  return {
    stop: () => {
      stopped = true;
      scheduler.reset();
    },
    done,
  };
}

/** Re-provision after an auth loss: 30 s, doubling, capped at 5 min (owner decision 2026-10-20). */
export const REPROVISION_RETRY = { minMs: 30_000, maxMs: 300_000 };
/** A tap or key press this recent means someone is at the screen: do not provision under them. */
export const REPROVISION_INTERACTION_QUIET_MS = 60_000;

export interface ReprovisionOptions {
  /** Current route; '/admin-setup' means an operator is doing the PIN flow — stand down. */
  getPath: () => string;
  /** ms since the last tap/key press on the screen; null when unknown. */
  msSinceInteraction: () => number | null;
}

/**
 * Re-provision a tablet whose session the server rejected (briefing #29).
 *
 * ── WAAROM ─────────────────────────────────────────────────────────────────
 *
 * Een 401 die de refresh niet kon herstellen wist de sessie (onAuthFailed). De
 * auto-provision liep alleen bij een koude start, dus de tablet bleef op /login
 * staan — zonder noodknop op het scherm, en zonder dat iemand het zag — tot
 * iemand de app herstartte (twee tablets zo sinds 2026-09-11).
 *
 * Hier: dezelfde auto-provision (zelfde UDID, zelfde IP + tweede factor op de
 * server), opnieuw met backoff zolang de tablet niet aangemeld is. Een 503 komt
 * hier nooit: die wist de sessie niet. Stopt bij succes, bij een token dat langs
 * een andere weg binnenkomt (PIN) en op /admin-setup. Zonder UDID kost een poging
 * geen request (de managed config kan nog binnenkomen), dus die blijft gewoon lopen.
 * Een serververdict (404/409) wordt NIET als einde behandeld: een operator kan
 * de koppeling intussen herstellen, dus het blijft proberen op het plafond.
 * Navigeert alleen naar '/' bij succes — nooit naar /admin-setup.
 */
export function startReprovision(
  deps: AutoProvisionDeps,
  opts: ReprovisionOptions,
  scheduler: RetryScheduler = createRetryScheduler(REPROVISION_RETRY),
): { stop: () => void } {
  let stopped = false;
  const stop = () => {
    stopped = true;
    scheduler.reset();
  };
  const quietDeps: AutoProvisionDeps = {
    ...deps,
    navigate: (path) => {
      if (path === '/') deps.navigate(path);
    },
  };

  const attempt = async (n: number): Promise<void> => {
    if (stopped) return;
    if (deps.hasToken()) {
      deps.log?.('reprovision.done', { reason: 'token-present', attempt: n });
      stop();
      return;
    }
    if (opts.getPath() === '/admin-setup') {
      deps.log?.('reprovision.done', { reason: 'admin-setup', attempt: n });
      stop();
      return;
    }
    const idle = opts.msSinceInteraction();
    if (idle !== null && idle < REPROVISION_INTERACTION_QUIET_MS) {
      deps.log?.('reprovision.deferred', { reason: 'user-interacting', attempt: n });
      scheduler.schedule(() => { void attempt(n); });
      return;
    }
    // attempt ≥ 1: runAutoProvision never re-navigates on failure for those.
    const outcome = await runAutoProvision(quietDeps, { attempt: n + 1 });
    if (stopped) return;
    if (outcome.provisioned || deps.hasToken()) {
      deps.log?.('reprovision.success', { attempt: n });
      stop();
      return;
    }
    const delayMs = scheduler.schedule(() => { void attempt(n + 1); });
    deps.log?.('reprovision.retry', { nextAttempt: n + 1, delayMs, retryable: outcome.retryable });
  };

  // First try after the first backoff step, not at once: the 401 that got us here
  // may be the tail of a server hiccup, and a burst of tablets all re-provisioning
  // in the same second is exactly what a recovering API does not need.
  scheduler.schedule(() => { void attempt(0); });
  return { stop };
}

// One re-provision loop per page; a second auth loss while it runs is a no-op.
let reprovisionLoop: { stop: () => void } | null = null;
let lastInteractionAt: number | null = null;
let interactionListening = false;
function listenForInteraction(): void {
  if (interactionListening || typeof window === 'undefined') return;
  interactionListening = true;
  const mark = () => { lastInteractionAt = Date.now(); };
  window.addEventListener('pointerdown', mark, { passive: true });
  window.addEventListener('keydown', mark);
}

/**
 * React hook: runs auto-provision once on cold start when unauthenticated.
 * Wire it into the boot sequence before the `/login` redirect (App.tsx).
 */
// MODULE-LEVEL guard: auto-provision must run at most ONCE per page load, even
// if AutoProvisionBoot unmounts/remounts (e.g. a transient redirect to /login).
// A per-component ref resets on remount and caused a tight re-provision loop
// (~1 req/s) when the session briefly dropped. This survives remounts; a real
// cold start is a fresh page load, which resets it.
let autoProvisionAttempted = false;

export function useAutoProvision(): void {
  const { api, accessToken, setAccessToken, setUser } = useAuth();
  const navigate = useNavigate();
  const ranRef = useRef(false);

  // The deps both loops share. Read through a ref so the re-provision loop,
  // which may start long after mount, uses the current router/api.
  const buildDeps = (): AutoProvisionDeps => ({
    log: logEvent,
    hasToken: () => Boolean(authStore.getState().accessToken),
    getUrlSearch: () => (typeof window !== 'undefined' ? window.location.search : ''),
    getStored: (key) => {
      try {
        return window.sessionStorage.getItem(key);
      } catch {
        return null;
      }
    },
    setStored: (key, value) => {
      try {
        window.sessionStorage.setItem(key, value);
      } catch {
        // private mode / storage disabled — UDID stays in memory for this run.
      }
    },
    stripUrl: () => {
      try {
        const params = new URLSearchParams(window.location.search);
        params.delete('hexnodeUDID');
        const qs = params.toString();
        const cleanUrl = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
        window.history.replaceState({}, '', cleanUrl);
      } catch {
        // history API unavailable — leave the URL as-is.
      }
    },
    // PRODUCTION-SAFETY: the native managed-config path is wired ONLY inside
    // the APK. On the browser fleet (Hexnode Website Kiosk / RPi Chromium)
    // `isNativePlatform()` is false, so this dep is `undefined` and step 2b in
    // runAutoProvision is structurally unreachable — the URL `?hexnodeUDID=`
    // path those 800 production devices use is left completely untouched.
    getManagedUdid: Capacitor.isNativePlatform()
      ? async () => {
          try {
            const ids = await KeepAliveBridge.getDeviceIds();
            // On-device evidence for the DebugOverlay: did the MDM deliver
            // the managed config at all? (2026-09-23: a whole site sat on
            // /admin-setup after a reboot and nothing server-side could say
            // whether the plugin call failed or the value never arrived.)
            logEvent('provision.managedRead', {
              androidId: ids.androidId,
              managedUdid: ids.managedUdid || '(empty)',
            });
            return ids.managedUdid || null;
          } catch (e) {
            logEvent('provision.managedRead', { error: (e as Error)?.message || String(e) });
            return null;
          }
        }
      : undefined,
    post: (url, body) => api.post(url, body),
    storeToken: (token) => {
      // The resident app uses the device JWT as its access token for
      // Authorization: Bearer (same store qrActivate / admin-setup write to).
      setAccessToken(token);
    },
    storeUser: (user) => {
      // setUser flips isAuthenticated → true and suppresses the /me hydration
      // that a DeviceToken can't satisfy. Without this the app bounces to /login.
      setUser(user as any);
    },
    navigate: (path) => navigate(path),
  });
  const buildDepsRef = useRef(buildDeps);
  buildDepsRef.current = buildDeps;

  // Briefing #29: a session the SERVER rejected is re-provisioned with backoff
  // instead of leaving the tablet on /login without its emergency button.
  useEffect(() => {
    listenForInteraction();
    return onDeviceAuthLost(() => {
      if (reprovisionLoop) return;
      logEvent('reprovision.start', {});
      const loop = startReprovision(buildDepsRef.current(), {
        getPath: () => (typeof window !== 'undefined' ? window.location.pathname : ''),
        msSinceInteraction: () => (lastInteractionAt == null ? null : Date.now() - lastInteractionAt),
      });
      // Free the slot once a token is back (by this loop or by the PIN flow), so a
      // LATER auth loss starts a fresh loop.
      const unsubscribe = authStore.subscribe((state) => {
        if (state.accessToken) {
          unsubscribe();
          loop.stop();
          reprovisionLoop = null;
        }
      });
      reprovisionLoop = loop;
    });
  }, []);

  useEffect(() => {
    if (ranRef.current || autoProvisionAttempted) return;
    // Runs once per page load (module guard survives remounts). On a cold start
    // (no token) it provisions; when already authenticated it REFRESHES the
    // profile (language/building/extension) if a hexnodeUDID is available —
    // runAutoProvision no-ops safely when there's a token but no UDID.
    ranRef.current = true;
    autoProvisionAttempted = true;

    // Read the LIVE store, not the render's `accessToken`: a retry may be
    // pending while an operator completes the PIN flow, and that must end the
    // loop (and turn the next run into a harmless refresh).
    startAutoProvision(buildDeps());
  }, [api, accessToken, setAccessToken, setUser, navigate]);
}
